import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { useProfile } from '../lib/profile';
import { Application, DesiredModule, SelfModule } from '../lib/types';
import { Badge, Card, Empty, Modal, Notice, PageHead } from '../components/ui';
import {
  ApplicationFormFields,
  ApplicationFormValues,
  ModuleRulesPicker,
  applicationFormComplete,
  applicationPayload,
  emptyApplicationForm,
} from '../components/ApplicationForm';

/** What an application's approval state means, in one word. */
export function ApplicationState({ application }: { application: Application }) {
  if (application.state === 'pending') return <Badge tone="warn">Waiting for approval</Badge>;
  if (application.state === 'disabled') return <Badge tone="danger">disabled</Badge>;
  return <Badge tone="ok">live</Badge>;
}

export function Applications() {
  const profile = useProfile();
  const applications = useAsync(() => api.get<Application[]>('/api/applications'));
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHead
        title="Applications"
        description={
          profile.isAdmin
            ? 'An application defines what is possible at all. Consents from users, API keys and user permissions can only narrow that scope.'
            : 'The applications you registered. Describe what one needs — an administrator decides whether it becomes real, and until then nothing of it is live.'
        }
        actions={
          <button className="primary" onClick={() => setCreating(true)}>
            Register application
          </button>
        }
      />

      {applications.error ? <Notice tone="danger">{applications.error}</Notice> : null}

      <Card tight>
        {applications.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Access paths</th>
                <th>Modules</th>
                <th className="num">Keys</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {applications.data.map((application) => (
                <tr key={application.id}>
                  <td>
                    <Link to={`/applications/${application.id}`}>{application.name}</Link>
                    {application.entra_client_id ? (
                      <div className="mono muted tiny">{application.entra_client_id}</div>
                    ) : null}
                  </td>
                  <td>
                    <div className="row">
                      {application.allow_client_credentials ? (
                        <Badge>Application permissions</Badge>
                      ) : null}
                      {application.allow_delegated ? <Badge tone="accent">delegated</Badge> : null}
                      {application.allow_api_key ? <Badge>API key</Badge> : null}
                    </div>
                  </td>
                  <td className="mono muted">
                    {application.moduleAccess.map((access) => access.module_key).join(', ') || '—'}
                  </td>
                  <td className="num">{application.apiKeyCount ?? 0}</td>
                  <td>
                    <ApplicationState application={application} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>
            {profile.isAdmin
              ? 'No application registered yet.'
              : 'You have not registered an application yet.'}
          </Empty>
        )}
      </Card>

      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            applications.reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const profile = useProfile();
  const modules = useAsync(() => api.get<SelfModule[]>('/api/me/modules'));
  const [values, setValues] = useState<ApplicationFormValues>(emptyApplicationForm());
  const [wanted, setWanted] = useState<DesiredModule[]>([]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/applications', {
        ...applicationPayload(values),
        modules: wanted,
        note: note || null,
      });
      onCreated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Register application"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button
            className="primary"
            onClick={() => void submit()}
            disabled={busy || !applicationFormComplete(values)}
          >
            {profile.isAdmin ? 'Register' : 'Submit for approval'}
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {profile.isAdmin ? null : (
        <Notice tone="info">Nothing here is live until an administrator approves it.</Notice>
      )}
      <ApplicationFormFields values={values} onChange={setValues} />
      <h4>Modules and permissions</h4>
      <ModuleRulesPicker modules={modules.data ?? []} value={wanted} onChange={setWanted} />
      <label className="field">
        <span>
          Why is this needed? <span className="hint">— read by whoever reviews it</span>
        </span>
        <textarea value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
    </Modal>
  );
}
